<template>
  <section class="sheet__spread-details">
    <div
      class="sheet__spread-card"
      :style="{
        borderColor: borderColor,
        background: backgroundColor
      }"
    >
      <div class="sheet__spread-header">
        <InitialAvatarIcon :icon="costs.icon" size="medium" />
        <BaseText
          class="sheet__spread-title"
          size="p4"
          :color="NebraskaColors.elementPrimary"
        >
          {{ costs.title }}
        </BaseText>
      </div>

      <BaseText
        :color="NebraskaColors.elementSecondary"
        size="p5"
        class="sheet__spread-text"
      >
        {{ costs.details }}
      </BaseText>
    </div>

    <svg width="0" height="0" aria-hidden="true">
      <defs>
        <linearGradient
          id="spread-icon-gradient"
          x1="0%"
          y1="0%"
          x2="70%"
          y2="0%"
        >
          <stop offset="0%" :stop-color="spreadGradientColorStart" />
          <stop offset="20%" :stop-color="spreadGradientColorMid" />
          <stop offset="100%" :stop-color="spreadGradientColorEnd" />
        </linearGradient>
      </defs>
    </svg>
  </section>
</template>

<script setup lang="ts">
import { IProductCostsDetails } from '@/modules/catalog/services/products';
import { useDashStore } from '@/modules/common/store/DashModule';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { BaseText, InitialAvatarIcon } from '@warrenbrasil/nebraska-web';
import { computed, PropType } from 'vue';

defineProps({
  costs: {
    type: Object as PropType<IProductCostsDetails>,
    default: null
  }
});

const theme = useDashStore();

const borderColor = computed(() =>
  theme.isDarkMode ? '#FFFFFFB2' : '#EBE7E6'
);

const backgroundColor = computed(() =>
  theme.isDarkMode ? '#FFFFFF1A' : '#FFFFFF33'
);

const spreadGradientColorStart = '#325580';
const spreadGradientColorMid = '#34639c';
const spreadGradientColorEnd = '#75b7e9';
</script>

<style lang="less" scoped>
.sheet {
  &__spread-text {
    color: @element-secondary;
    white-space: pre-line;
  }

  &__spread-details {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: @size-spacing-x450;
  }

  &__spread-details::before {
    content: '';
    position: absolute;
    right: -180px;
    bottom: -150px;
    width: 800px;
    height: 250px;
    background: linear-gradient(
      320deg,
      #32557f 7.17%,
      #4079bf 16.22%,
      @background-secondary 70.53%
    );
    background-blend-mode: overlay;
    backdrop-filter: blur(40px);
    -webkit-backdrop-filter: blur(40px);
    filter: blur(40px);
    -webkit-filter: blur(40px);
    will-change: filter, transform;
  }

  &__spread-card {
    position: relative;
    border: @size-border-x400 solid;
    border-radius: @size-radius-x300;
    padding: @size-spacing-x500;
  }

  &__spread-header {
    display: flex;
    align-items: center;
    gap: @size-spacing-x300;
    margin-bottom: @size-spacing-x300;
  }

  &__spread-title {
    font-weight: 600;
  }
}

:deep(.initial-avatar) {
  width: @size-spacing-x500;
  height: @size-spacing-x500;
}

:deep(.initial-avatar-icon) {
  background-color: transparent !important;
}

:deep(.initial-avatar-icon .svg-icon *) {
  fill: url(#spread-icon-gradient);
}
</style>
